import React, { useState } from 'react';
import { X, Building, CreditCard, Shield, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import './ChangeBankAccountModal.css';

const ChangeBankAccountModal = ({ isOpen, onClose, onSuccess, currentAccount }) => {
  const { getAxios } = useSecureAuth();
  const [formData, setFormData] = useState({
    accountNumber: '',
    bankCode: '',
    bankName: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifiedAccountName, setVerifiedAccountName] = useState('');
  const [error, setError] = useState('');

  // Nigerian banks
  const banks = [
    { name: 'Paystack Test Bank (TEST ONLY)', code: '001' },
    { name: 'Access Bank', code: '044' },
    { name: 'Citibank', code: '023' },
    { name: 'Ecobank', code: '050' },
    { name: 'Fidelity Bank', code: '070' },
    { name: 'First Bank of Nigeria', code: '011' },
    { name: 'First City Monument Bank (FCMB)', code: '214' },
    { name: 'Globus Bank', code: '00103' },
    { name: 'Guaranty Trust Bank (GTBank)', code: '058' },
    { name: 'Heritage Bank', code: '030' },
    { name: 'Keystone Bank', code: '082' },
    { name: 'Kuda Bank', code: '50211' },
    { name: 'Opay', code: '999992' },
    { name: 'Palmpay', code: '999991' },
    { name: 'Polaris Bank', code: '076' },
    { name: 'Providus Bank', code: '101' },
    { name: 'Stanbic IBTC Bank', code: '221' },
    { name: 'Standard Chartered Bank', code: '068' },
    { name: 'Sterling Bank', code: '232' },
    { name: 'Union Bank', code: '032' },
    { name: 'United Bank for Africa (UBA)', code: '033' },
    { name: 'Unity Bank', code: '215' },
    { name: 'Wema Bank', code: '035' },
    { name: 'Zenith Bank', code: '057' }
  ];

  if (!isOpen) return null;

  const handleClose = () => {
    setFormData({ accountNumber: '', bankCode: '', bankName: '' });
    setError('');
    setVerified(false);
    setVerifiedAccountName('');
    onClose();
  };

  const handleBankChange = (e) => {
    const selectedBankCode = e.target.value;
    const selectedBank = banks.find(b => b.code === selectedBankCode);
    setFormData(prev => ({
      ...prev,
      bankCode: selectedBankCode,
      bankName: selectedBank ? selectedBank.name : ''
    }));
    setVerified(false);
    setVerifiedAccountName('');
    setError('');
  };

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, accountNumber: value }));
    setVerified(false);
    setVerifiedAccountName('');
    setError('');
  };

  const handleVerify = async () => {
    setVerifying(true);
    setError('');

    try {
      const axios = getAxios();
      const response = await axios.post('/kyc/verify-bank', {
        accountNumber: formData.accountNumber,
        bankCode: formData.bankCode,
        bankName: formData.bankName
      });

      if (response.data.success) {
        setVerified(true);
        setVerifiedAccountName(response.data.data.accountName);
        setError('');
      } else {
        setError(response.data.error || 'Verification failed');
        setVerified(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify account. Please try again.');
      setVerified(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!verified) {
      setError('Please verify your account first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // The account is already verified and saved by the verify endpoint
      // Just call onSuccess to refresh the KYC status
      await onSuccess();
      handleClose();
    } catch (err) {
      setError('Failed to update account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canVerify = formData.accountNumber.length === 10 && formData.bankCode;

  return (
    <div className="change-bank-overlay" onClick={handleClose}>
      <div className="change-bank-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={handleClose}>
          <X size={20} />
        </button>

        <div className="modal-icon">
          <Building size={32} />
        </div>

        <h2 className="modal-title">Change Bank Account</h2>
        <p className="modal-message">
          Update your settlement bank account. The new account will be verified before being saved.
        </p>

        {currentAccount && (
          <div className="current-account-info">
            <span className="current-label">Current Account:</span>
            <span className="current-value">****{currentAccount.accountNumber?.slice(-4)}</span>
            <span className="current-bank">{currentAccount.bankName}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="change-bank-form">
          <div className="form-group">
            <label htmlFor="bank">Select Bank</label>
            <div className="select-wrapper">
              <Building size={18} className="select-icon" />
              <select
                id="bank"
                value={formData.bankCode}
                onChange={handleBankChange}
                required
                disabled={loading || verifying}
              >
                <option value="">Choose your bank</option>
                {banks.map(bank => (
                  <option key={bank.code} value={bank.code}>
                    {bank.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="accountNumber">Account Number</label>
            <div className="input-wrapper">
              <CreditCard size={18} className="input-icon" />
              <input
                id="accountNumber"
                type="text"
                value={formData.accountNumber}
                onChange={handleAccountNumberChange}
                placeholder="10-digit account number"
                maxLength={10}
                required
                disabled={loading || verifying}
              />
            </div>
          </div>

          {canVerify && !verified && (
            <button
              type="button"
              onClick={handleVerify}
              className="verify-btn"
              disabled={verifying}
            >
              {verifying ? (
                <>
                  <Loader size={18} className="spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Shield size={18} />
                  Verify Account
                </>
              )}
            </button>
          )}

          {verified && verifiedAccountName && (
            <div className="verification-success">
              <CheckCircle size={18} />
              <div>
                <strong>Account Verified!</strong>
                <p>{verifiedAccountName}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !verified}
            >
              {loading ? 'Updating...' : 'Update Account'}
            </button>
          </div>
        </form>

        <div className="security-notice">
          <Shield size={14} />
          <span>Account changes are logged for security</span>
        </div>
      </div>
    </div>
  );
};

export default ChangeBankAccountModal;
